import { Close } from '@mui/icons-material';
import { Box, Dialog, DialogTitle, IconButton, Typography } from '@mui/material';
import { maskEmail, maskPhoneNumber } from 'utils';
import OTPInput from 'react-otp-input';
import { Fragment, useEffect, useRef, useState } from 'react';
import { ErrorToaster, SuccessToaster } from 'components/toaster/Toaster';
import AuthServices from 'apis/auth/AuthServices';
import Colors from 'assets/style/Colors';
import { PrimaryButton } from 'components/buttons/Buttons';

const OtpBox = ({ value, setValue }) => {
  return (
    <Box sx={{ my: 2 }}>
      <OTPInput
        value={value}
        numInputs={5}
        onChange={setValue}
        inputStyle={{
          width: "50px",
          height: "50px",
          fontSize: "26px",
          backgroundColor: '#eaeaea',
          color: "#14212a",
          border: "none",
          borderRadius: '8px',
          outline: "none",
        }}
        focusStyle={{
          width: "50px",
          height: "50px",
          fontSize: "26px",
          outline: "none",
          border: "none",
          borderRadius: '8px',
        }}
        containerStyle={{ display: "flex", justifyContent: "space-evenly" }}
        renderInput={(props) => <input {...props} />}
      />
    </Box>
  )
}

function VerifyOtp({ open, loading, error, otpNumber, email, register }) {

  const intervalRef = useRef(null);

  const [showButton, setShowButton] = useState(false);

  const [resendLoading, setResendLoading] = useState(false);

  // *For OTP Value
  const [numberOtp, setNumberOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');

  // *For Resend OTP
  const [resendOtpTimer, setResendOtpTimer] = useState(true);
  const [mins, setMins] = useState();
  const [sec, setSec] = useState();

  const [attempts, setAttempts] = useState(0);

  // *For Otp Timer
  const otpTimer = (customDuration) => {
    try {
      const duration = customDuration || 60 * 2 // *Set OTP Time Duration
      let timer = duration, minutes, seconds;

      if (intervalRef.current) clearInterval(intervalRef.current);

      intervalRef.current = setInterval(function () {
        minutes = parseInt(timer / 60, 10);
        seconds = parseInt(timer % 60, 10);

        minutes = minutes < 10 ? "0" + minutes : minutes;
        seconds = seconds < 10 ? "0" + seconds : seconds;
        setMins(minutes)
        setSec(seconds)
        if (--timer < 0) {
          setResendOtpTimer(false)
          clearInterval(intervalRef.current)
          intervalRef.current = null;
        }
      }, 1000);
    } catch (error) {
      ErrorToaster(error)
    }
  }

  // *For Reset OTP Value
  const resetOtpValue = () => {
    setNumberOtp('')
    setEmailOtp('')
  }

  // *For Resend OTP
  const resendOtp = async () => {
    setResendLoading(true)
    try {
      if (otpNumber) {
        const { message } = await AuthServices.sendWhatsAppOtp({ phone_number: otpNumber })
        SuccessToaster(message)
      }
      if (email) {
        const { message } = await AuthServices.sendEmailOtp({ email: email })
        SuccessToaster(message)
      }

      const nextWait = attempts === 1 ? 180 : attempts === 2 ? 300 : 600;
      setAttempts(prev => prev + 1);
      otpTimer(nextWait)
      setResendOtpTimer(true)
    } catch (error) {
      ErrorToaster(error)
    } finally {
      setResendLoading(false)
    }
  }

  // *For Verify OTP
  const handleVerify = () => {
    try {
      const payload = {}
      if (otpNumber) {
        payload.whatsappOtpCode = numberOtp
      }
      if (email) {
        payload.emailOtpCode = emailOtp
      }
      register(payload)
    } catch (error) {
      console.log(' numberOtp => error:', error)
    }
  }

  useEffect(() => {
    otpTimer()
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (error) {
      resetOtpValue()
    }
  }, [error]);

  useEffect(() => {
    const inputPhoneLength = otpNumber ? numberOtp?.length === 5 ? true : false : true
    const inputEmailLength = email ? emailOtp?.length === 5 ? true : false : true
    if (inputPhoneLength && inputEmailLength) {
      setShowButton(true)
    } else {
      setShowButton(false)
    }
  }, [numberOtp, emailOtp]);

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      sx={{ '& .MuiDialog-paper': { width: '80%', height: "auto", borderRadius: 2, py: { xs: 2, md: 4 }, px: { xs: 3, md: 6 } } }}
    >
      <DialogTitle sx={{ textAlign: "center", fontSize: '18px', fontWeight: 700 }}>OTP Verification</DialogTitle>
      <Box sx={{ mb: 2 }}>
        {otpNumber &&
          <>
            <Typography sx={{ fontSize: 13, textAlign: 'center' }}>
              OTP has been sent to your
              preferred whatsapp number {maskPhoneNumber(otpNumber)}
            </Typography>
            <OtpBox
              value={numberOtp}
              setValue={(val) => setNumberOtp(val)}
            />
          </>
        }
        {email &&
          <>
            <Typography sx={{ fontSize: 13, textAlign: 'center' }} >
              OTP has been sent to your
              preferred email {maskEmail(email)}
            </Typography>
            <OtpBox
              value={emailOtp}
              setValue={(val) => setEmailOtp(val)}
            />
          </>
        }
        {error &&
          <Typography color="error" sx={{ fontSize: 12, textAlign: 'center' }}>
            You have entered invalid OTP code
          </Typography>
        }
      </Box>
      <Box sx={{ mb: 2 }}>
        {resendOtpTimer ? (
          <Typography sx={{ fontSize: 13, textAlign: 'center' }}>
            {mins}:{sec}
          </Typography>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Typography sx={{ fontSize: 13, textAlign: 'center', mb: 1 }} >
              Don’t received OTP code?
            </Typography>
            <PrimaryButton
              title="Resend Code"
              type='button'
              loading={resendLoading}
              btnColor={Colors.secondary}
              onClick={() => resendOtp()}
            />
          </Box>
        )}
      </Box>
      {showButton &&
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <PrimaryButton
            title="Verify"
            type='button'
            loading={loading}
            btnColor={Colors.primaryGradient}
            onClick={() => handleVerify()}
          />
        </Box>
      }
    </Dialog>
  )
}

export default VerifyOtp